import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import RadarWaterfall from "components/field/RadarWaterfall";
import SoilSurfaceControl from "components/field/SoilSurfaceControl";
import { useSoilSurface } from "hooks/useSoilSurface";
import { rtctApi } from "services/rtctApi";
import { decodeWaterfall } from "utils/radarStream";
import { soilLine } from "utils/soilSurface";
import { useStableState } from "utils/stableState";

// The radar over the last week on the node page: the burst history binned on the server (GET radar/heatmap/, one
// column an hour, a few tens of kB), drawn by Radar analysis' own waterfall with our estimate and the radar's pick.
// Where a node sends only the raw beat signal, a column is our FFT of it on the radar's scale.
const DAYS = 7;
const POLL_MS = 30 * 60 * 1000;
const LABELS = {
  aria: "Radar, last 7 days: point at a column to read it",
  empty: () => "No radar bursts in the last 7 days.",
  hint: "Point at the heatmap to read a column.",
  pick: "Radar's own pick",
  ours: "Our estimate",
  gap: "No burst that hour",
  caption: () =>
    "One column per hour, distance from the radar down, weak (dark) to strong (yellow). Each column is the mean spectrum of that hour's bursts; where the node sends only its raw beat signal, it is our FFT of it.",
};

// The endpoint's answer in the waterfall's shape, with our estimate on each column.
export function heatmapData(data) {
  const wf = decodeWaterfall(data);
  const times = Array.isArray(data?.t) ? data.t : [];
  const ours = Array.isArray(data?.our_mm) ? data.our_mm : [];
  const byTime = new Map(times.map((t, i) => [t, ours[i]]));
  return {
    ...wf,
    columns: wf.columns.map((c) => {
      const v = byTime.get(c.t);
      return { ...c, ours: typeof v === "number" && Number.isFinite(v) ? v : null };
    }),
  };
}

export default function RadarHeatmapCard({ mac }) {
  const [state, setState] = useStableState({ mac: "", data: null, error: "" });
  useEffect(() => {
    if (!mac || typeof rtctApi.radarHeatmap !== "function") return undefined;
    let abort = new AbortController();
    const load = () => {
      abort.abort();
      abort = new AbortController();
      const signal = abort.signal;
      Promise.resolve(rtctApi.radarHeatmap(mac, signal, { days: DAYS }))
        .then((data) => {
          if (!signal.aborted) setState({ mac, data, error: "" });
        })
        .catch((e) => {
          if (!signal.aborted) setState((old) => ({ ...old, mac, error: e?.message || "Radar history unavailable" }));
        });
    };
    load();
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, POLL_MS);
    return () => {
      clearInterval(timer);
      abort.abort();
    };
  }, [mac, setState]);
  const raw = state.mac === mac ? state.data : null;
  const wf = useMemo(() => (raw ? heatmapData(raw) : null), [raw]);
  const now = raw?.to ? Date.parse(raw.to) : Date.now();
  const soil = useSoilSurface(mac, DAYS);
  const lines = useMemo(() => {
    const line = soilLine(soil.soil);
    return line ? [line] : undefined;
  }, [soil.soil]);
  return (
    <section className="field-panel" aria-label="Radar, last 7 days">
      <div className="field-panel-head">
        <div>
          <h2>Radar, last 7 days</h2>
          <p>Spectrum per hour, with our estimate and the radar's pick</p>
        </div>
      </div>
      <div className="field-panel-body">
        {state.error && state.mac === mac && <div className="field-alert">{state.error}</div>}
        {!wf && !state.error && <p className="field-caption">Loading…</p>}
        {wf && <RadarWaterfall data={wf} minutes={DAYS * 1440} range="near" now={now} labels={LABELS} lines={lines} />}
        <SoilSurfaceControl soil={soil} />
        <p className="field-caption">
          Every burst and capture in <Link to="/radar-analysis">Radar analysis</Link>.
        </p>
      </div>
    </section>
  );
}
